import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  ChevronLeft,
  Webhook,
  FormInput,
  CheckCircle2,
  Copy
} from 'lucide-react';

const DocumentationPage = () => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  // Add noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back to Sitemap */}
        <a 
          href="/sitemap" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#10c0a5] mb-8"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Sitemap
        </a>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#e6faf7] mb-4">
            <FileText className="w-8 h-8 text-[#10c0a5]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Documentation
          </h1>
          <p className="text-slate-600 text-lg">
            Technical documentation for forms, Zapier integration, and development guidelines.
          </p>
        </motion.div>

        {/* Zapier Integration */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Webhook className="w-6 h-6 text-[#10c0a5]" />
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Zapier Integration
            </h2>
          </div>

          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Setting Up Zapier Webhooks</h3>
            <p className="text-slate-600 mb-4">
              Configure Zapier webhooks to receive form submissions. Use the following API endpoints:
            </p>
            
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-slate-900">Partner Form Webhook</span>
                  <button 
                    onClick={() => copyToClipboard('POST /api/config/zapier\n{"webhook_url": "YOUR_ZAPIER_WEBHOOK_URL"}')}
                    className="text-slate-400 hover:text-[#10c0a5]"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <code className="text-sm text-slate-600 block bg-slate-50 p-2 rounded">
                  POST /api/config/zapier<br/>
                  {`{"webhook_url": "YOUR_ZAPIER_WEBHOOK_URL"}`}
                </code>
              </div>
              
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-slate-900">Waitlist Form Webhook</span>
                  <button 
                    onClick={() => copyToClipboard('POST /api/config/zapier-waitlist\n{"webhook_url": "YOUR_ZAPIER_WEBHOOK_URL"}')}
                    className="text-slate-400 hover:text-[#10c0a5]"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <code className="text-sm text-slate-600 block bg-slate-50 p-2 rounded">
                  POST /api/config/zapier-waitlist<br/>
                  {`{"webhook_url": "YOUR_ZAPIER_WEBHOOK_URL"}`}
                </code>
              </div>
            </div>
          </div>

          {/* Partner Form Fields */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-slate-900 mb-4">Partner Form - Zapier Fields</h3>
            <p className="text-slate-600 mb-4">
              The following fields are sent to Zapier when a partner application is submitted:
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-900">Field Name</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-900">Type</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-900">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="py-2 px-3 font-mono text-xs">ID</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Unique submission ID (UUID)</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Partner Type</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Software Provider, Marketing Professional, Developer, IMO/BGA, Add a Product, Other Partnership</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Name</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Contact full name</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Email</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Contact email address</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Phone</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Phone number formatted as (XXX) XXX-XXXX</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Company Name</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Business/company name</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Website</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Company website URL (optional)</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Comments</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Additional comments (optional)</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Submitted At</td><td className="py-2 px-3">ISO DateTime</td><td className="py-2 px-3 text-slate-600">Timestamp of submission</td></tr>
                </tbody>
              </table>
            </div>

            <h4 className="font-semibold text-slate-900 mt-6 mb-3">Category-Specific Fields</h4>
            <p className="text-slate-600 mb-4">
              Additional fields are sent based on the selected partner type:
            </p>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Software Provider</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Software Type:</strong> crm, quoting, agency management, lead management, compliance, other software</li>
                  <li>• <strong>Integration Experience:</strong> yes multiple platforms, yes a few, no but interested, no</li>
                  <li>• <strong>User Base:</strong> under 100, 100 500, 500 1000, 1000+</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Marketing Professional</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Marketing Services:</strong> lead generation, website design, seo sem, social media, content marketing, full service</li>
                  <li>• <strong>Insurance Experience:</strong> yes extensive, yes some, no but interested</li>
                  <li>• <strong>Client Size:</strong> individual agents, small agencies, mid agencies, large agencies, all sizes</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Developer</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Tech Stack:</strong> Free text (e.g., React, Python, Node.js)</li>
                  <li>• <strong>Insurance Api Experience:</strong> yes extensive, yes some, no but interested</li>
                  <li>• <strong>Integration Interest:</strong> api integration, custom development, white label, consulting, all</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">IMO / BGA</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Agent Count:</strong> under 50, 50 200, 200 500, 500 1000, 1000+</li>
                  <li>• <strong>Carrier Contracts:</strong> Free text</li>
                  <li>• <strong>Regions Served:</strong> national, multi state, single state</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Add a Product</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Product Type:</strong> life insurance, annuity, disability, ltc, other</li>
                  <li>• <strong>Life Insurance Type:</strong> term, whole life, universal life, indexed ul, variable ul, final expense, guaranteed issue, multiple, not applicable</li>
                  <li>• <strong>Can Provide Rates:</strong> yes ready, yes need time, need discussion, not sure</li>
                  <li>• <strong>Api Access:</strong> yes full, yes limited, in development, no</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Other Partnership</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• <strong>Business Description:</strong> Free text</li>
                  <li>• <strong>Partnership Idea:</strong> Free text</li>
                  <li>• <strong>Value Proposition:</strong> Free text</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Waitlist Form Fields */}
          <div className="bg-slate-50 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Waitlist Form - Zapier Fields</h3>
            <p className="text-slate-600 mb-4">
              The following fields are sent to Zapier when a waitlist signup is submitted:
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-900">Field Name</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-900">Type</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-900">Possible Values</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="py-2 px-3 font-mono text-xs">ID</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">UUID</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Name</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Full name</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Email</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Email address</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Phone</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">(XXX) XXX-XXXX format</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Company Name</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Business name</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Plan Interest</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">starter, professional, team</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Has Website</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Yes, No</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Website URL</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">URL (if has website)</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Uses CRM</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Yes, No</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">CRM Name</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">e.g., Go High Level, Zoho, Digital BGA, Pipedrive</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Agency Size</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">1, 2-5, 6-10, 11-25, 26-50, 50+</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">States Licensed</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">1-5, 6-10, 11-25, 26-40, 41-50, all</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Runs Paid Ads</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Yes, No</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Ad Platforms</td><td className="py-2 px-3">String</td><td className="py-2 px-3 text-slate-600">Comma-separated: Facebook, Google, Instagram, TikTok, YouTube, LinkedIn, Twitter/X, Other: [custom]</td></tr>
                  <tr><td className="py-2 px-3 font-mono text-xs">Submitted At</td><td className="py-2 px-3">ISO DateTime</td><td className="py-2 px-3 text-slate-600">Timestamp</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Validation Rules */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 className="w-6 h-6 text-[#10c0a5]" />
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Validation Rules
            </h2>
          </div>

          <div className="bg-slate-50 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Field Validation</h3>
            
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Email</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Must be a valid email format (e.g., name@domain.com)</li>
                  <li>• Required field</li>
                  <li>• Validated using standard email regex</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Phone Number</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Minimum 10 digits required</li>
                  <li>• Auto-formatted to (XXX) XXX-XXXX</li>
                  <li>• Only digits, spaces, dashes, parentheses, and + allowed</li>
                  <li>• Mobile devices show numeric keypad (type="tel" with inputMode="numeric")</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Website URL</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Optional field</li>
                  <li>• Must be valid URL format if provided</li>
                  <li>• Accepts with or without http:// or https://</li>
                  <li>• Example: https://example.com or www.example.com</li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <h5 className="font-medium text-slate-900 mb-2">Name / Company Name</h5>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Minimum 2 characters required</li>
                  <li>• Required field</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Creating New Forms */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <FormInput className="w-6 h-6 text-[#10c0a5]" />
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Creating Additional Forms
            </h2>
          </div>

          <div className="bg-slate-50 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Form Development Guidelines</h3>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-slate-900 mb-2">1. Component Structure</h4>
                <p className="text-sm text-slate-600 mb-2">Each form should include:</p>
                <ul className="text-sm text-slate-600 space-y-1 ml-4">
                  <li>• Multi-step wizard with progress indicator</li>
                  <li>• React Hook Form with Zod validation</li>
                  <li>• Framer Motion animations for step transitions</li>
                  <li>• Mobile-responsive design with back buttons</li>
                  <li>• Height communication for iframe embedding</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-2">2. Backend API Pattern</h4>
                <p className="text-sm text-slate-600 mb-2">For each new form, create:</p>
                <ul className="text-sm text-slate-600 space-y-1 ml-4">
                  <li>• Pydantic model for submission data</li>
                  <li>• POST endpoint for submissions (e.g., /api/[form-name])</li>
                  <li>• GET endpoint for retrieving submissions</li>
                  <li>• Zapier webhook config endpoint</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-2">3. Zapier Integration</h4>
                <p className="text-sm text-slate-600 mb-2">To enable Zapier:</p>
                <ul className="text-sm text-slate-600 space-y-1 ml-4">
                  <li>• Send data as query parameters (not JSON body) for field parsing</li>
                  <li>• Use readable field names with spaces (e.g., "Company Name" not "company_name")</li>
                  <li>• Flatten nested objects into individual fields</li>
                  <li>• Store webhook URL in MongoDB webhook_config collection</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-2">4. Embedding</h4>
                <p className="text-sm text-slate-600 mb-2">For iframe embedding:</p>
                <ul className="text-sm text-slate-600 space-y-1 ml-4">
                  <li>• Include postMessage for height updates</li>
                  <li>• Include scrollToTop message for step navigation</li>
                  <li>• Set scrolling="no" on iframe</li>
                  <li>• Add resize event listener on parent page</li>
                </ul>
              </div>

              <div>
                <h4 className="font-medium text-slate-900 mb-2">5. Styling</h4>
                <p className="text-sm text-slate-600 mb-2">Brand consistency:</p>
                <ul className="text-sm text-slate-600 space-y-1 ml-4">
                  <li>• Primary color: #10c0a5</li>
                  <li>• Hover color: #0ea892</li>
                  <li>• Font: Manrope for headings, Inter for body</li>
                  <li>• Use Shadcn UI components from /components/ui/</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Embed Code Template */}
        <section className="mb-12">
          <h3 className="font-semibold text-slate-900 mb-4">Embed Code Template</h3>
          <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto">
            <pre className="text-sm text-slate-300">
{`<iframe 
  id="quoteplicity-widget"
  src="https://YOUR-DOMAIN.com/PAGE-PATH" 
  width="100%" 
  frameborder="0" 
  scrolling="no"
  style="border: none; overflow: hidden; min-height: 800px;"
></iframe>

<script>
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'resize') {
      document.getElementById('quoteplicity-widget').style.height = e.data.height + 'px';
    }
    if (e.data && e.data.type === 'scrollToTop') {
      document.getElementById('quoteplicity-widget').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
</script>`}
            </pre>
          </div>
        </section>

        {/* Testing Guidelines */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 className="w-6 h-6 text-[#10c0a5]" />
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Testing Guidelines
            </h2>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-amber-800 mb-3">⚠️ Important: Test on Deployed Domains</h3>
            <p className="text-amber-700 mb-4">
              Always test forms on the <strong>deployed production domains</strong> in addition to preview/staging environments. 
              Production environments may have different configurations, database states, or Zapier webhook settings.
            </p>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <h4 className="font-medium text-slate-900 mb-2">Testing Checklist:</h4>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>Test form submissions on the live production URL</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>Verify Zapier webhooks are configured for production</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>Check that submissions appear in Zapier after production tests</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>Confirm data is stored in the Submissions Dashboard</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#10c0a5] mt-0.5 flex-shrink-0" />
                  <span>Test on mobile devices for responsive behavior</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Production URLs</h3>
            <div className="space-y-3">
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <span className="font-medium text-slate-900">Partner Form:</span>
                <code className="ml-2 text-sm text-[#10c0a5]">https://partners.quoteplicity.com/</code>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <span className="font-medium text-slate-900">Waitlist Form:</span>
                <code className="ml-2 text-sm text-[#10c0a5]">https://partners.quoteplicity.com/waitlist</code>
              </div>
              <div className="bg-white rounded-lg p-4 border border-slate-200">
                <span className="font-medium text-slate-900">Submissions Dashboard:</span>
                <code className="ml-2 text-sm text-[#10c0a5]">https://partners.quoteplicity.com/admin/submissions</code>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DocumentationPage;
